import { type FormikValues, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import Icon from '@components/Icon/Icon';
import { EIcons } from '@enums/icons.enum';

import { ErrorMessage } from '../index';
import classes from './FormRate.module.scss';

type Props = {
	name: string;
	firstValue?: number;
	max?: number;
	onChangeCallBack?: (name: string, value: number) => void;
	displayError?: boolean;
	disabled?: boolean;
};

const FormRate: React.FC<Props> = ({
	name,
	firstValue,
	max = 5,
	onChangeCallBack,
	displayError = true,
	disabled = false,
}: React.PropsWithChildren<Props>) => {
	const { setFieldTouched, setFieldValue, errors, touched } = useFormikContext<FormikValues>();
	const [value, setValue] = useState<number | undefined>(firstValue);
	const [prevFirstValue, setPrevFirstValue] = useState(firstValue);

	const isError = !!touched[name] && !!errors[name];

	if (firstValue !== prevFirstValue) {
		setPrevFirstValue(firstValue);
		setValue(firstValue);
	}

	useEffect(() => {
		setFieldValue(name, firstValue);
	}, [firstValue]);

	const handlePress = (next: number) => {
		next = value === undefined || next > value ? next : next - 1;
		setValue(next);
		setFieldValue(name, next);
		setFieldTouched(name, true, false);
		onChangeCallBack?.(name, next);
	};

	return (
		<div className={classes['container']}>
			<div className={classes['starsContainer']}>
				{Array.from({ length: max }, (_, index) => {
					const starValue = index + 1;
					const isActive = !!value && starValue <= value;
					return (
						<button
							key={starValue}
							type="button"
							className={classes['starButton']}
							onClick={() => handlePress(starValue)}
							disabled={disabled}
						>
							<Icon name={EIcons.Star} size={28} color={isActive ? 'var(--star)' : 'var(--medium)'} />
						</button>
					);
				})}
			</div>
			{displayError && <ErrorMessage error={errors[name] as string | undefined} visible={isError} />}
		</div>
	);
};

export default FormRate;
